// AnnounceEntryModal — 入室時に重要 announce を 1 回だけブロッキング表示する
// 中央モーダル (2026-06-06 consult: announce-entry-modal-2026-06-06.md).
//
// 目的: 全ルーム宛の「お知らせ」を入室時だけ **バナーより目立つ** モーダルで
// 提示する. 満たすべき 2 点 = (1) 見逃してほしくない (2) 描画中に邪魔したくない.
//
// 振る舞い (入室直後の受付ウィンドウ方式):
//   - announce は server (JoinService) が `room.welcome` の **後** に unicast で
//     遅れて (= 別フレームで逐次) 送ってくる. mount 一発で active 集合を凍結する
//     方式だと「凍結時点でまだ届いていない / 一部しか届いていない」ことが多く、
//     モーダルが出ない / 一部しか出ないレースになる. これを避けるため、mount から
//     `ENTRY_WINDOW_MS` の **受付ウィンドウ** を開き、その間に届いた announce を
//     id 単位で逐次バッチに積む.
//   - **入室バッチ** = ウィンドウが開いている間に active へ現れた
//     severity ∈ {warning, critical} の announce (= 届いた順, id 先勝ち, 10 件 cap).
//     バッチが非空になった時点でモーダル表示. ウィンドウ内に遅れて届いた 2 件目
//     以降も **同じモーダルに追加表示** される.
//   - **ウィンドウが閉じた後** に新規に現れた announce は live 扱いでバッチに
//     入れない (= バナーのみ、描画中の邪魔をしない要件を維持).
//   - severity='info' は対象外 (= バナーのみ、従来通り静か).
//   - 複数同時 (max 10) は 1 枚のモーダル内に縦積みでまとめる. 各カードは
//     濃紺 inset 背景 + 左に severity 色の細帯 + 単色グリフ + 白文字の落ち着いた
//     アクセント帯スタイル (= 家の配色トークン参照). 高彩度の AnnounceBanner とは
//     別意図 (バナー=注意喚起 / モーダル=描画を邪魔せず静かに伝える) で、モーダル
//     のみトーンを落とす.
//   - 「閉じる」で dismiss (= memory only、localStorage 未使用). dismiss 後は
//     ウィンドウ内で新着が来ても再ポップしない. モーダルを閉じても
//     AnnounceBanner はそのまま残り、後から参照できる.
//   - 再入室 = 新しい RoomSession / 新しい mount = 新しいウィンドウなので再表示
//     される (要件に合致).
//   - z-[60]: 既存 modal (z-50) より上に重ねる帯 (ConfirmDialog と同流儀).

import type { SystemAnnouncePosted } from '@oekaki/protocol';
import { useEffect, useId, useState } from 'react';

import { linkifyMessage } from './linkifyMessage.js';

/**
 * モーダル本文のリンクスタイル. カード本文は白 (= var(--color-text)) なので、
 * リンクはアクセント黄 + 下線で控えめに浮かせる (= severity 帯の色は壊さない).
 */
const MODAL_LINK_CLASS =
  'text-[var(--color-accent)] underline underline-offset-2 hover:brightness-110';

export interface AnnounceEntryModalProps {
  /**
   * モーダルが閉じられたときに 1 回だけ呼ばれる (2026-08-12)。**入室時モーダルの
   * 二重表示を防ぐ**ために使う — レイヤー可視の確認ダイアログ (B-2) は、この
   * announce モーダルが出ていない / 閉じられた後にだけ出す。
   */
  onDismissed?: () => void;
  readonly activeAnnounces: readonly SystemAnnouncePosted[];
  /**
   * このタブで「見せて閉じた」announce id の集合 (memory only)。RoomLayout より上位
   * (RootSwitch) が所有する mutable Set で、自動再接続の fallback join が phase を
   * `joining` 経由にして RoomLayout ごと remount しても生き残る。含まれる id は
   * 入室バッチに積まない (= 再ポップしない)。手動の入室 / 退室で上位がクリアする。
   */
  readonly shownIds?: Set<string>;
}

/** shownIds に未登録でモーダル対象 (warning/critical) の announce が残っているか. */
export const hasPendingEntryAnnounce = (
  announces: readonly SystemAnnouncePosted[],
  shownIds: ReadonlySet<string>,
): boolean => selectEntryAnnounces(announces).some((a) => !shownIds.has(a.id));

/**
 * 入室直後の受付ウィンドウ幅 (ms). announce は welcome 後に unicast で逐次届く
 * ため、mount からこの間に届いたものを「入室バッチ」として同じモーダルに集約する.
 * 2500ms: welcome 直後の再送が遅延しても拾える余裕を持ちつつ、入室から十分早く
 * 閉じて「描き始めてから遅れて出るバッチ」を作らない値.
 */
export const ENTRY_WINDOW_MS = 2500;

/** モーダルで強調する severity (= info はバナーのみ、対象外). */
const MODAL_SEVERITIES: ReadonlySet<NonNullable<SystemAnnouncePosted['severity']>> = new Set([
  'warning',
  'critical',
]);

// severity → アクセント色 (= 家の配色トークン参照). べた塗りせず、左帯 +
// 小アイコンの **色シグナル** だけに使う. カード背景は濃紺 inset で統一し、
// 本文は白 (= var(--color-text)). バナー (AnnounceBanner) は高彩度のまま据え置き、
// モーダルだけ落ち着いたアクセント帯にする.
// info は MODAL_SEVERITIES 対象外なので実際には warning/critical のみ使われるが、
// 型の網羅性のため全 severity を持つ (info は無難な muted 色).
const SEVERITY_ACCENT: Record<NonNullable<SystemAnnouncePosted['severity']>, string> = {
  info: 'var(--color-text-muted)',
  warning: 'var(--color-accent)', // 黄 #f5c842
  critical: 'var(--color-error)', // 赤 #c83232
};

/**
 * severity を表す控えめな単色グリフ (= currentColor 継承の inline SVG).
 * emoji は環境依存の色になるので避け、左帯と同じ severity 色 (= currentColor)
 * で染まる三角警告マークを使う. 左帯が主シグナルなのでアイコンは小さく控えめ.
 */
const SeverityGlyph = (): React.ReactNode => (
  <svg
    viewBox="0 0 16 16"
    width="14"
    height="14"
    aria-hidden="true"
    className="mt-0.5 shrink-0"
    fill="currentColor"
  >
    {/* 角丸三角 + 中央の感嘆符 (! は背景色で抜く). */}
    <path d="M7.13 1.7 0.6 13.1a1 1 0 0 0 .87 1.5h13.06a1 1 0 0 0 .87-1.5L8.87 1.7a1 1 0 0 0-1.74 0Z" />
    <rect x="7.1" y="5.4" width="1.8" height="4.2" rx="0.9" fill="var(--color-bg-soft)" />
    <circle cx="8" cy="11.6" r="1" fill="var(--color-bg-soft)" />
  </svg>
);

/** モーダルに積めるバッチ上限 (= server cap と整合). */
const BATCH_CAP = 10;

/**
 * announce 集合から「モーダルで強調する候補」を抽出する純粋関数.
 * - severity ∈ {warning, critical} のみ
 * - id 重複は先勝ちで除外 (= 防御的、reducer 側で upsert 済だが念のため)
 * - 10 件で切る
 *
 * 旧 API. 受付ウィンドウ方式では `accumulateEntryAnnounces` が中心だが、
 * 「ある時点の active 集合に対する一発抽出」自体は引き続き有用なので残す.
 */
export const selectEntryAnnounces = (
  announces: readonly SystemAnnouncePosted[],
): readonly SystemAnnouncePosted[] => accumulateEntryAnnounces([], announces, true);

/**
 * 受付ウィンドウ方式の蓄積ロジック (純粋関数).
 *
 * `prevBatch` (= これまでに積んだ入室バッチ) に、`newActiveAnnounces` のうち
 * まだ入っていない severity ∈ {warning, critical} の announce を **届いた順 / id
 * 先勝ち** で追加して返す. 入室直後に welcome 後 unicast で逐次届く announce を、
 * 同じバッチへ少しずつ積み上げるのが狙い.
 *
 * - `windowOpen === false` のときは何も足さず `prevBatch` をそのまま返す
 *   (= ウィンドウが閉じた後に現れた announce は live 扱いでバナー任せ).
 * - 既に `prevBatch` に居る id は再追加しない (= id 先勝ち, dedup).
 * - 上限 `BATCH_CAP` (10) を超えない. 既に上限なら `prevBatch` をそのまま返す.
 * - 変化が無ければ参照同一の `prevBatch` を返す (= 不要な再 render を避ける).
 */
export const accumulateEntryAnnounces = (
  prevBatch: readonly SystemAnnouncePosted[],
  newActiveAnnounces: readonly SystemAnnouncePosted[],
  windowOpen: boolean,
): readonly SystemAnnouncePosted[] => {
  if (!windowOpen) return prevBatch;
  if (prevBatch.length >= BATCH_CAP) return prevBatch;

  const seen = new Set(prevBatch.map((a) => a.id));
  const added: SystemAnnouncePosted[] = [];
  for (const a of newActiveAnnounces) {
    if (prevBatch.length + added.length >= BATCH_CAP) break;
    const severity = a.severity ?? 'info';
    if (!MODAL_SEVERITIES.has(severity)) continue;
    if (seen.has(a.id)) continue;
    seen.add(a.id);
    added.push(a);
  }
  if (added.length === 0) return prevBatch;
  return [...prevBatch, ...added];
};

export interface AnnounceEntryModalViewProps {
  readonly entries: readonly SystemAnnouncePosted[];
  readonly onClose: () => void;
  readonly titleId: string;
}

/**
 * 純粋な presentational view (hooks 無し). ConfirmDialog と同流儀で stateful
 * wrapper から切り出し、tree-walking テストで検証する.
 */
export const AnnounceEntryModalView = ({
  entries,
  onClose,
  titleId,
}: AnnounceEntryModalViewProps): React.ReactNode => {
  if (entries.length === 0) return null;
  return (
    // z-[60]: 既存 modal (z-50) の上に重ねる (ConfirmDialog と同帯).
    <div className="fixed inset-0 z-[60] grid place-items-center p-4">
      {/* Backdrop. ConfirmDialog と同流儀の a11y compliant button. */}
      <button
        type="button"
        aria-label="閉じる"
        tabIndex={-1}
        onClick={onClose}
        className="-z-10 absolute inset-0 cursor-default bg-black/60"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        // w-full max-w + min-w-0: 固定 w-[440px] + max-w-full だけでは不足だった
        // (2026-07-03 実機 fb)。grid/flex の子は min-width:auto なので、message 内の
        // 長い無改行トークン (URL 等) が min-content 幅を押し上げると max-w を貫通して
        // 画面外へはみ出し「閉じる」ボタンが押せなくなる。min-w-0 で intrinsic min を
        // 殺し、本文側は break-words で折り返す (絵ログモーダルで実証済みのパターン)。
        className="flex max-h-[90vh] w-full min-w-0 max-w-[440px] flex-col overflow-y-auto rounded border border-white/10 bg-[var(--color-panel)] p-5 shadow-xl"
      >
        <h2 id={titleId} className="mb-3 text-base font-bold text-[var(--color-accent)]">
          お知らせ
        </h2>
        <div className="flex flex-col gap-2">
          {entries.map((a) => {
            const severity = a.severity ?? 'info';
            const accent = SEVERITY_ACCENT[severity];
            return (
              // 濃紺 inset カード + 左に 3px severity 色帯. アイコンと帯は同じ
              // severity 色 (= accent), 本文は白. data-severity は test/デバッグ用.
              <div
                key={a.id}
                data-severity={severity}
                className="flex items-start gap-2 rounded px-3 py-2 text-sm font-bold text-[var(--color-text)]"
                style={{
                  backgroundColor: 'var(--color-bg-soft)',
                  borderLeft: `3px solid ${accent}`,
                  color: accent,
                }}
              >
                {/* アイコンだけ severity 色 (= currentColor 継承). */}
                <SeverityGlyph />
                {/* 本文は白に戻す (= 上位 color を accent から上書き).
                    min-w-0: flex 子の intrinsic min を殺す (長い URL がカードごと
                    横に押し広げるのを防ぎ、break-words で折り返させる). */}
                <span className="min-w-0 break-words whitespace-pre-wrap text-[var(--color-text)]">
                  {linkifyMessage(a.message, { linkClassName: MODAL_LINK_CLASS })}
                </span>
              </div>
            );
          })}
        </div>
        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded bg-[var(--color-accent)] px-4 py-2 text-sm font-bold text-[var(--color-panel)] transition hover:brightness-110"
          >
            閉じる
          </button>
        </div>
      </div>
    </div>
  );
};

export const AnnounceEntryModal = ({
  activeAnnounces,
  onDismissed,
  shownIds,
}: AnnounceEntryModalProps): React.ReactNode => {
  const titleId = useId();
  // 入室直後の受付ウィンドウ. mount で開き、ENTRY_WINDOW_MS 後に閉じる.
  const [windowOpen, setWindowOpen] = useState(true);
  // ウィンドウ中に逐次積み上げる入室バッチ (= モーダルに出す announce).
  const [batch, setBatch] = useState<readonly SystemAnnouncePosted[]>([]);
  // dismiss は memory only (= localStorage 未使用、同 mount 内で再ポップしない).
  const [dismissed, setDismissed] = useState(false);

  // ENTRY_WINDOW_MS でウィンドウを閉じる. mount 一回だけ.
  useEffect(() => {
    const timer = setTimeout(() => setWindowOpen(false), ENTRY_WINDOW_MS);
    return () => clearTimeout(timer);
  }, []);

  // activeAnnounces が変わるたび、ウィンドウが開いていればバッチへ逐次追加.
  // accumulate は windowOpen=false / 変化なしのとき参照同一の prev を返すので、
  // setBatch は実質 no-op (= React が bail out) になる. windowOpen を deps に
  // 含めるのは、閉じた瞬間の最終 reconcile を明示するため (= 閉後は no-op).
  useEffect(() => {
    // 既読 (= このタブで見せて閉じた) id は積まない. 自動再接続で remount されても
    // 再ポップさせないため. 新しい id は従来どおり積む.
    const fresh = shownIds ? activeAnnounces.filter((a) => !shownIds.has(a.id)) : activeAnnounces;
    setBatch((prev) => accumulateEntryAnnounces(prev, fresh, windowOpen));
  }, [activeAnnounces, windowOpen, shownIds]);

  if (dismissed) return null;

  return (
    <AnnounceEntryModalView
      entries={batch}
      titleId={titleId}
      onClose={() => {
        for (const a of batch) shownIds?.add(a.id);
        setDismissed(true);
        onDismissed?.();
      }}
    />
  );
};
